import { Suspense, lazy, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStringVariation, useBucketeerClient } from '@bucketeer/react-client-sdk';
import CreateAccountDemoAutoLogin from '@/components/CreateAccountDemoAutoLogin';
import DLoading from '@/components/DLoading';
import { BUCKETEER_FEATURE_IDS } from '@/utils/bucketeer';
import mixpanel from '@/utils/mixpanel';

// v1 (NewOnboarding)
const NewOnboardingV1 = lazy(() => import('@/pages/NewOnboarding'));
const OnboardingStartedV1 = lazy(() =>
  import('@/pages/NewOnboarding/OnoboardingStarted')
);
const AccountVerificationV1 = lazy(() =>
  import('@/pages/NewOnboarding/AccountVerification')
);
const AccountCreationV1 = lazy(() =>
  import('@/pages/NewOnboarding/AccountCreation')
);
const OnboardingNoWebsiteV1 = lazy(() =>
  import('@/pages/NewOnboarding/OnboardingNoWebsite')
);

// v2 (v3-dashboard Onboarding)
const NewOnboardingV2 = lazy(() =>
  import('@/v3-dashboard/pages/Onboarding')
);
const OnboardingStartedV2 = lazy(() =>
  import('@/v3-dashboard/pages/Onboarding/OnboardingStarted')
);
const AccountVerificationV2 = lazy(() =>
  import('@/v3-dashboard/pages/Onboarding/AccountVerification')
);
const AccountCreationV2 = lazy(() =>
  import('@/v3-dashboard/pages/Onboarding/AccountCreation')
);
const OnboardingNoWebsiteV2 = lazy(() =>
  import('@/v3-dashboard/pages/Onboarding/OnboardingNoWebsite')
);

const PAGE_MAP = {
  main: {
    'onboarding-v1': NewOnboardingV1,
    'onboarding-v2': NewOnboardingV2,
  },
  started: {
    'onboarding-v1': OnboardingStartedV1,
    'onboarding-v2': OnboardingStartedV2,
  },
  verify: {
    'onboarding-v1': AccountVerificationV1,
    'onboarding-v2': AccountVerificationV2,
  },
  claim: {
    'onboarding-v1': AccountCreationV1,
    'onboarding-v2': AccountCreationV2,
  },
  'no-website': {
    'onboarding-v1': OnboardingNoWebsiteV1,
    'onboarding-v2': OnboardingNoWebsiteV2,
  },
};

/**
 * Renders the onboarding page for the given `page` based on Bucketeer flags.
 * - `onboarding-demo-account`: `onboarding` (default) uses `onboarding-version` (v1 vs v2).
 *   `demo-account` on `page="main"` only: auto sign-in with the shared demo user.
 * - `onboarding-version`: falls back to `onboarding-v2` if unknown or SDK unavailable.
 * @param {Object} props
 * @param {'main'|'started'|'verify'|'claim'|'no-website'} props.page - Which onboarding step to render
 */
const OnboardingVersionLayout = ({ page = 'main' }) => {
  const client = useBucketeerClient();
  const onboardingDemoAccount = useStringVariation(BUCKETEER_FEATURE_IDS.ONBOARDING_DEMO_ACCOUNT, 'onboarding');
  const resolvedOnboardingDemoAccount =
    onboardingDemoAccount === 'demo-account' ? 'demo-account' : 'onboarding';

  const version = useStringVariation(BUCKETEER_FEATURE_IDS.ONBOARDING_VERSION, 'onboarding-v2');
  const components = PAGE_MAP[page];
  const resolvedVersion = components?.[version] ? version : 'onboarding-v2';
  const Component = components[resolvedVersion];
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!PAGE_MAP[page] || page !== 'main') {
      return;
    }

    // Option A: Wait for the Bucketeer client to be ready so we don't
    // track the default value before the real evaluation arrives.
    if (!client) {
      return;
    }

    // Option B: Only fire the assignment event once per mount to prevent
    // duplicate tracking if resolvedVersion changes after the first render.
    if (hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;

    mixpanel.track('AB Test Variant Assigned', {
      experiment_name: 'Onboarding Demo Account',
      variant: resolvedOnboardingDemoAccount,
      source: 'bucketeer',
    });

    mixpanel.people.set({
      'Onboarding Demo Account': resolvedOnboardingDemoAccount,
    });

    if (resolvedOnboardingDemoAccount === 'onboarding') {
      mixpanel.track('AB Test Variant Assigned', {
        experiment_name: 'Signup Variant',
        variant: resolvedVersion,
        source: 'bucketeer',
      });

      mixpanel.people.set({
        'Signup Variant': resolvedVersion,
      });
    }
  }, [page, resolvedVersion, resolvedOnboardingDemoAccount, client]);

  if (!PAGE_MAP[page]) {
    return null;
  }

  if (!client) {
    return <DLoading />;
  }

  if (page === 'main' && resolvedOnboardingDemoAccount === 'demo-account') {
    return <CreateAccountDemoAutoLogin />;
  }

  return (
    <Suspense fallback={<DLoading />}>
      <Component />
    </Suspense>
  );
};

OnboardingVersionLayout.propTypes = {
  page: PropTypes.oneOf(['main', 'started', 'verify', 'claim', 'no-website'])
};

export default OnboardingVersionLayout;
